import { useEffect, useState } from 'react';
import { UserAddress } from '../../../clients/quipli-service/user/user';
import { Card } from '../../../components/card/card';
import { Button, EcommerceButton } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { Radio } from '../../../components/radio/radio';
import { Typography } from '../../../components/typography/typography';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { AccountAddressForm } from './account-adress-form';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { Location } from '../../../clients/quipli-service/location/location';
import { DeliveryLocation } from '../../../clients/quipli-service/delivery/delivery';
import classNames from 'classnames';
import { formatPhoneNumber } from '../../../util/util';

export const AccountAddresses = ({
  addresses,
  enableSelect,
  onSelect,
  onOpenForm,
  selectedAddress,
  type,
  reload,
  location,
}: {
  addresses: UserAddress[];
  enableSelect: boolean;
  onSelect?: (address?: UserAddress) => void;
  onOpenForm?: () => void;
  selectedAddress?: UserAddress;
  type: 'billing' | 'delivery';
  reload?: () => Promise<void>;
  location: Location;
}) => {
  const { getConfiguration, listDeliveryLocations } = useCustomerAPI();

  const [isLocationsModuleEnabled, setIsLocationsModuleEnabled] = useState<boolean>(false);
  const [deliveryLocations, setDeliveryLocations] = useState<DeliveryLocation[]>([]);

  const [view, setView] = useState<'list' | 'form'>('list');
  const [editableAddress, setEditableAddress] = useState<UserAddress>();

  const handleOpenForm = (address?: UserAddress) => {
    setEditableAddress(address);
    setView('form');
    onOpenForm?.();
    onSelect?.(undefined);
  };

  const selectDefaultOrExisting = () => {
    if (!enableSelect) return;

    if (addresses.length === 0) {
      onSelect?.(undefined);
      return;
    }

    const existingSelectedAddress = selectedAddress ? addresses.find((a) => a.id === selectedAddress.id) : undefined;
    onSelect?.(existingSelectedAddress || addresses.find((a) => a.isDefault) || addresses[0]);
  };

  useEffect(() => {
    const getSettings = async () => {
      const isEnabled = (await getConfiguration('CONF_DELIVERY_LOCATIONS_MODULE'))?.value === '1';
      setIsLocationsModuleEnabled(isEnabled);

      if (type === 'delivery' && isEnabled) {
        const dl = await listDeliveryLocations();
        setDeliveryLocations(dl);
      }
    };

    getSettings();
    view === 'form' && setView('list');
  }, []);

  useEffect(() => {
    if (view === 'list') {
      addresses.length > 0 ? selectDefaultOrExisting() : handleOpenForm();
    }
  }, [view, addresses]);

  return (
    <>
      {view === 'list' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4 px-3">
            {addresses.map((address) => (
              <AddressCard
                key={address.id}
                address={address}
                enableSelect={enableSelect}
                isSelected={selectedAddress?.id === address.id}
                onSelect={(value) => onSelect?.(value)}
                onEdit={(value) => {
                  handleOpenForm(value);
                }}
                reload={reload}
                isLocationsModuleEnabled={isLocationsModuleEnabled}
              />
            ))}
          </div>

          <div className="flex justify-between items-center pb-3 px-3">
            <EcommerceButton
              buttonStyle="tertiary"
              onClick={() => {
                handleOpenForm();
              }}
            >
              <Typography type="text-sm" weight="medium">
                Add New Address
              </Typography>
            </EcommerceButton>
          </div>
        </>
      ) : (
        <AccountAddressForm
          address={editableAddress}
          type={type}
          enableDuplicate={type === 'delivery' || (type === 'billing' && location.allowDeliverySelection)}
          isLocationsModuleEnabled={isLocationsModuleEnabled}
          deliveryLocations={deliveryLocations}
          onConfirm={async () => {
            await reload?.();
            onSelect?.(undefined);
            setEditableAddress(undefined);
            setView('list');
          }}
          onClose={() => {
            if (addresses.length > 0) {
              setEditableAddress(undefined);
              onSelect?.(undefined);
              setView('list');
            }
          }}
        />
      )}
    </>
  );
};

const AddressCard = ({
  address,
  enableSelect,
  isSelected = false,
  onSelect,
  onEdit,
  reload,
  isLocationsModuleEnabled,
}: {
  address: UserAddress;
  enableSelect: boolean;
  isSelected?: boolean;
  onSelect: (address: UserAddress) => void;
  onEdit: (address: UserAddress) => void;
  reload?: () => Promise<void>;
  isLocationsModuleEnabled: boolean;
}) => {
  const { deleteUserAddress } = useCustomerAPI();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  return (
    <>
      <Card
        className={classNames(
          'border-solid border border-grey-300 cursor-pointer hover:border-primary-500 transition-colors duration-200 ease-in-out',
          { 'border-primary-500': isSelected },
        )}
        onClick={() => enableSelect && onSelect(address)}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-start gap-3">
            {enableSelect && (
              <div className="p-1">
                <Radio name={`${address.type}-address`} isChecked={isSelected} type="ecommercePrimary" />
              </div>
            )}

            <div className="grow flex flex-col gap-2">
              <Typography type="text-sm" weight="medium">
                {address.name}
              </Typography>
              {address.companyName && <Typography type="text-sm">Company name: {address.companyName}</Typography>}
              <Typography type="text-sm">{formatPhoneNumber(address.phone)}</Typography>
              <Typography type="text-sm">
                {address.address1 != '-1'
                  ? address.address1
                  : isLocationsModuleEnabled
                  ? address.customDeliveryLocation
                  : ''}
                {!address.isGoogleGenerated &&
                  `${address.address2 ? `\n${address.address2}` : ''}${address.city ? `\n${address.city}` : ''}${
                    address.state ? `, ${address.state}` : ''
                  }${address.zip ? `\n ${address.zip}` : ''}`}
              </Typography>
            </div>

            <div className="flex flex-row">
              <Button className="icon-button" onClick={() => onEdit(address)}>
                <Icon icon="Edit" stroke="var(--grey-400)" />
              </Button>

              <Button
                className="icon-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteModalOpen(true);
                }}
              >
                <Icon icon="Trash" stroke="var(--grey-400)" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <ConfirmModal
        useEcommerceTheme
        isOpen={isDeleteModalOpen}
        close={() => setIsDeleteModalOpen(false)}
        prompt="Delete Address"
        additionalText="Are you sure you want to delete this address?"
        headerIcon={<Icon icon="Trash" stroke="var(--error-500)" size="large" />}
        onConfirm={() => {
          deleteUserAddress(address.id).then(() => {
            setIsDeleteModalOpen(false);
            reload?.();
          });
        }}
      />
    </>
  );
};
